import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import {
  createOrderParcel,
  deleteOrderParcel,
  updateOrderParcel,
} from "@/api/orders";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  ParcelEditRow,
  PARCEL_DETAIL_LIST_H,
  PARCEL_GRID_COLUMNS,
  parcelCellCls,
} from "./shared/ParcelEditRow";
import { formatParcelWeight, sumParcelWeight } from "./shared/parcelTotals";
import { toastError } from "@/lib/toast";
import { extractApiError, type TranslateFn } from "@/lib/apiErrors";
import type { Parcel, ParcelWritePayload } from "@/types/orders";

interface Props {
  orderId: number;
  parcels: Parcel[];
  /** Whether the current user may add/edit/delete parcels. */
  canEdit: boolean;
  /**
   * Tenant `barcode_generation_mode === "order_number"` — drives the footer
   * caption «Штрихкоды из номера заказа». Passed in rather than queried here so
   * the block does not fire a second copy of a request the host already made.
   */
  barcodesFromOrderNumber?: boolean;
}

interface RowDraft {
  code: string;
  description: string;
  weight_kg: string;
}

/** A row that exists only in the browser until its first successful save. */
interface NewRow extends RowDraft {
  /** Local identity — never sent to the server. */
  key: string;
  error: string | null;
  saving: boolean;
}

const headCls =
  "text-[10px] font-semibold uppercase tracking-wider text-gray-400";

/**
 * Reads the duplicate-parcel-code message out of a failed save.
 *
 * BUG-465: this used to read `data.code[0]` directly. The backend's duplicate-code error is
 * raised as `{"code": ["..."]}`, where `code` is the PARCEL code — a domain field colliding
 * with the error envelope's `code` slot. The exception handler now relocates that domain
 * message into `non_field_errors` and puts the real registry code in the slot, so reading
 * `data.code[0]` would yield the first CHARACTER of `"VALIDATION_ERROR"`. Delegating to the
 * shared helper is therefore the correct reader, not merely a tidy-up.
 *
 * Returns null when the server said nothing usable, so the caller falls back to the toast.
 */
function extractCodeError(err: unknown, t: TranslateFn): string | null {
  const result = extractApiError(err, t);
  return result.isGeneric ? null : result.message;
}

function toPayload(draft: RowDraft): ParcelWritePayload {
  return {
    code: draft.code.trim(),
    description: draft.description.trim(),
    weight_kg:
      draft.weight_kg.trim() === "" ? undefined : draft.weight_kg.trim(),
  };
}

function draftOf(parcel: Parcel): RowDraft {
  return {
    code: parcel.code ?? "",
    description: parcel.description ?? "",
    /*
      DRF sends a `Decimal(_, 3)` as a STRING with all three decimals — "2.500".
      That needs 75px in the 56px weight track, so the value rendered clipped
      (measured on order 94 at 1280: scrollWidth 75 vs clientWidth 56, every
      row). `formatParcelWeight` drops the trailing zeros without inventing
      precision, so "2.500" shows as "2.5" — the same rendering the footer total
      and the create modal's «Вес, кг» already use, which is also why the three
      cannot now disagree about the same number.
    */
    weight_kg:
      parcel.weight_kg == null || parcel.weight_kg.trim() === ""
        ? ""
        : formatParcelWeight(Number(parcel.weight_kg)),
  };
}

/**
 * Whether two weight strings mean the same weight.
 *
 * Compared as NUMBERS, not as text: the row displays the server's `"1.500"` as
 * `"1.5"` (see {@link draftOf}), so a character comparison would call every
 * untouched row dirty and PATCH a reformatted weight back on any edit to a
 * neighbouring field. Blank is its own value — `Number("")` is 0, which would
 * make "no weight" equal to "0 kg".
 */
function sameWeight(a: string, b: string): boolean {
  const x = a.trim();
  const y = b.trim();
  if (x === "" || y === "") return x === y;
  return Number(x) === Number(y);
}

function sameDraft(a: RowDraft, b: RowDraft): boolean {
  return (
    a.code.trim() === b.code.trim() &&
    a.description.trim() === b.description.trim() &&
    sameWeight(a.weight_kg, b.weight_kg)
  );
}

/**
 * Editable parcels block for the order detail modal — the create modal's TABLE
 * (owner, 2026-08-25: «сделай добавление посылок на просмотре так же, как при
 * создании заказа — такой же паттерн, а то сейчас 2 разных подхода на одно и то
 * же действие»).
 *
 * Every row is editable in place, sharing {@link ParcelEditRow} with
 * {@link ParcelRowsEditor} so one action has one appearance on both surfaces.
 * «+ Посылка» appends a row to the list itself and focuses it; there is no
 * separate draft panel and no per-row Отмена/Сохранить pair — the row IS the
 * editor. The rejected build had a bordered panel hanging below a list that
 * simultaneously read «Посылок пока нет», so the block contradicted itself on
 * screen while the dispatcher was typing into it.
 *
 * **What is NOT shared is the data flow.** The create modal only builds form
 * state and submits once; this block PERSISTS each row on its own. A row commits
 * on blur — `POST` for a row that has no server id yet, `PATCH` for one that
 * does — and the trash sends `DELETE` (after a confirm for a saved row). Sharing
 * an interaction model does not mean sharing a persistence model.
 *
 * Read-only when {@link Props.canEdit} is false: the same table renders with
 * disabled inputs and no trash, so a dispatcher without the right sees the same
 * data in the same shape rather than a differently-built block.
 */
export function ParcelsEditSection({
  orderId,
  parcels,
  canEdit,
  barcodesFromOrderNumber = false,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /** Locally-added rows, not yet persisted. */
  const [newRows, setNewRows] = useState<NewRow[]>([]);
  /** Per-saved-parcel local edits, keyed by parcel id. */
  const [edits, setEdits] = useState<Record<number, RowDraft>>({});
  /** Per-saved-parcel inline error, keyed by parcel id. */
  const [rowErrors, setRowErrors] = useState<Record<number, string | null>>({});
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);

  /**
   * The row to focus once it renders.
   *
   * The list is a FIXED two-row viewport that scrolls, so on an order that
   * already fills it the appended row lands below the fold: without focusing and
   * scrolling to it, «+ Посылка» looks like it did nothing. That is the same
   * class of defect as content hidden behind `overflow: hidden`, which this
   * dialog hit twice today.
   */
  const focusKeyRef = useRef<string | null>(null);
  const inputRefs = useRef(new Map<string, HTMLInputElement>());

  useEffect(() => {
    const key = focusKeyRef.current;
    if (!key) return;
    const input = inputRefs.current.get(key);
    if (!input) return;
    focusKeyRef.current = null;
    // `block: "nearest"` brings the row into the viewport without yanking the
    // dialog when the row is already visible.
    //
    // Guarded because jsdom does not implement `scrollIntoView` at all: calling
    // it unguarded throws inside the effect and takes the whole component down
    // with it, which is what three tests here caught. Scrolling is a progressive
    // enhancement on top of the focus — FOCUS is the part that must not be lost,
    // so it runs either way.
    input.scrollIntoView?.({ block: "nearest" });
    input.focus();
  }, [newRows.length]);

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    await queryClient.invalidateQueries({ queryKey: ["orders"] });
  }, [queryClient, orderId]);

  const createMutation = useMutation({
    mutationFn: (payload: ParcelWritePayload) =>
      createOrderParcel(orderId, payload),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      parcelId,
      payload,
    }: {
      parcelId: number;
      payload: ParcelWritePayload;
    }) => updateOrderParcel(orderId, parcelId, payload),
  });

  const deleteMutation = useMutation({
    mutationFn: (parcelId: number) => deleteOrderParcel(orderId, parcelId),
    onSuccess: async () => {
      await invalidate();
      setPendingDelete(null);
    },
    onError: () => {
      setPendingDelete(null);
      toastError(t("orders.detail.parcels.deleteError"));
    },
  });

  function addRow() {
    const key = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    focusKeyRef.current = key;
    setNewRows((rows) => [
      ...rows,
      {
        key,
        code: "",
        description: "",
        weight_kg: "",
        error: null,
        saving: false,
      },
    ]);
  }

  function patchNewRow(key: string, patch: Partial<NewRow>) {
    setNewRows((rows) =>
      rows.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  }

  /**
   * Commits a locally-added row. A blank row is left alone — a dispatcher who
   * clicks «+ Посылка» and tabs straight past it has not asked for anything, and
   * `POST`ing an empty parcel would only produce a server-side validation error
   * they did not cause.
   */
  async function commitNewRow(row: NewRow) {
    if (row.saving) return;
    if (row.code.trim() === "" && row.description.trim() === "") return;
    patchNewRow(row.key, { saving: true, error: null });
    try {
      await createMutation.mutateAsync(toPayload(row));
      await invalidate();
      /*
        The row disappears from `newRows` only once the server has it — at which
        point the refreshed `parcels` prop renders it as a saved row. Dropping it
        before the refetch would blank the row for a frame.
      */
      setNewRows((rows) => rows.filter((r) => r.key !== row.key));
    } catch (err) {
      /*
        A failed save KEEPS the half-typed row exactly as it is, with the reason
        rendered inside it. A duplicate barcode is the common case and is fixed by
        editing one field, not by retyping the row — so nothing is cleared here.
      */
      const codeError = extractCodeError(err, t);
      patchNewRow(row.key, { saving: false, error: codeError });
      if (!codeError) toastError(t("orders.detail.parcels.saveError"));
    }
  }

  /** Commits an edit to an already-saved row, if it actually changed. */
  async function commitEdit(parcel: Parcel) {
    const draft = edits[parcel.id];
    const original = draftOf(parcel);
    if (!draft || sameDraft(draft, original)) return;
    /*
      Send back the server's OWN weight string when the weight was not actually
      changed. The row displays `"1.500"` as `"1.5"`, and PATCHing that
      re-rendered value on an edit to the description would rewrite a field the
      dispatcher never touched — numerically identical, but still a write the
      audit log would record as a change. Only a genuinely edited weight is sent
      in the form it was typed.
    */
    const payload = toPayload(draft);
    if (sameWeight(draft.weight_kg, original.weight_kg)) {
      payload.weight_kg =
        parcel.weight_kg == null || parcel.weight_kg.trim() === ""
          ? undefined
          : parcel.weight_kg;
    }
    try {
      await updateMutation.mutateAsync({
        parcelId: parcel.id,
        payload,
      });
      await invalidate();
      setEdits(({ [parcel.id]: _drop, ...rest }) => rest);
      setRowErrors((e) => ({ ...e, [parcel.id]: null }));
    } catch (err) {
      const codeError = extractCodeError(err, t);
      setRowErrors((e) => ({ ...e, [parcel.id]: codeError }));
      if (!codeError) toastError(t("orders.detail.parcels.saveError"));
    }
  }

  // Totals count the saved rows AND the locally-added ones, so the footer
  // matches what is on screen rather than what the server has seen so far.
  const totalCount = parcels.length + newRows.length;
  const totalWeight = sumParcelWeight([
    ...parcels.map((p) => ({ weight_kg: p.weight_kg ?? "" })),
    ...newRows.map((r) => ({ weight_kg: r.weight_kg })),
  ]);

  return (
    <section className="space-y-2">
      {/*
        Header — «ПОСЫЛКИ 2 посылки» (mockup, 2026-08-25). The count sits INSIDE
        the heading rather than beside it, so a screen reader announces the block
        and its size in one breath.
      */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="min-w-0 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
          {t("orders.detail.parcels.title")}{" "}
          {/* The count is the block's live subject, not a status — so it takes
              the dealer's runtime brand token, never a fixed colour (§10.10). */}
          <span className="font-semibold normal-case tracking-normal text-brand-primary">
            {t("orders.detail.parcels.placeCount", { count: totalCount })}
          </span>
        </h3>
      </div>

      <div className="space-y-2 rounded-xl border border-gray-200 p-3">
        {/* Column headers, sharing the row's tracks so the two cannot drift. */}
        <div
          className="grid gap-1.5 px-0.5"
          style={{ gridTemplateColumns: PARCEL_GRID_COLUMNS }}
        >
          <span className={headCls}>{t("orders.detail.parcels.code")}</span>
          <span className={headCls}>
            {t("orders.detail.parcels.descriptionLabel")}
          </span>
          <span className={headCls}>{t("orders.detail.parcels.weight")}</span>
          <span />
        </div>

        {/*
          THE dialog's only scroll container, and the one block with a FIXED
          height rather than a content-driven one: exactly two rows, always — see
          `PARCEL_DETAIL_VISIBLE_ROWS` for why the detail view shows two where the
          create modal shows four. A 40-parcel order scrolls here and nowhere
          else, and a 1-parcel order reserves the same box instead of collapsing —
          so adding or removing a row never moves the column's bottom edge, which
          is what keeps the three columns aligned (owner, 2026-08-25).
        */}
        <div
          data-testid="parcel-view-scroll"
          aria-label={t("orders.detail.parcels.listLabel")}
          className="space-y-1.5 overflow-y-auto overflow-x-hidden"
          style={{ height: `${PARCEL_DETAIL_LIST_H}px` }}
        >
          {/*
            The empty state appears only when there is genuinely nothing on
            screen — an unsaved new row counts. The rejected build showed
            «Посылок пока нет» beside a row the dispatcher was typing into.
          */}
          {parcels.length === 0 && newRows.length === 0 ? (
            <p className="flex h-full items-center justify-center px-2 text-center text-xs text-gray-400">
              {t("orders.detail.parcels.empty")}
            </p>
          ) : (
            <>
              {parcels.map((parcel, index) => {
                const rowNo = index + 1;
                const draft = edits[parcel.id] ?? draftOf(parcel);
                const setField = (patch: Partial<RowDraft>) =>
                  setEdits((e) => ({
                    ...e,
                    [parcel.id]: { ...draft, ...patch },
                  }));
                return (
                  <ParcelEditRow
                    key={parcel.id}
                    error={rowErrors[parcel.id] ?? null}
                    code={
                      <input
                        aria-label={t("orders.detail.parcels.codeRow", {
                          row: rowNo,
                        })}
                        className={parcelCellCls}
                        disabled={!canEdit}
                        value={draft.code}
                        onChange={(e) => setField({ code: e.target.value })}
                        onBlur={() => void commitEdit(parcel)}
                      />
                    }
                    description={
                      <input
                        aria-label={t("orders.detail.parcels.descriptionRow", {
                          row: rowNo,
                        })}
                        placeholder={t(
                          "orders.detail.parcels.descriptionPlaceholder",
                        )}
                        className={parcelCellCls}
                        disabled={!canEdit}
                        value={draft.description}
                        onChange={(e) =>
                          setField({ description: e.target.value })
                        }
                        onBlur={() => void commitEdit(parcel)}
                      />
                    }
                    weight={
                      <input
                        aria-label={t("orders.detail.parcels.weightRow", {
                          row: rowNo,
                        })}
                        type="number"
                        min="0"
                        step="0.001"
                        className={`${parcelCellCls} px-2 tabular-nums`}
                        disabled={!canEdit}
                        value={draft.weight_kg}
                        onChange={(e) =>
                          setField({ weight_kg: e.target.value })
                        }
                        onBlur={() => void commitEdit(parcel)}
                      />
                    }
                    action={
                      canEdit ? (
                        <button
                          type="button"
                          aria-label={`${t("orders.detail.parcels.delete")} — ${
                            parcel.code || t("orders.detail.noCode")
                          }`}
                          onClick={() => setPendingDelete(parcel.id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-brand-danger/10 hover:text-brand-danger"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      ) : undefined
                    }
                  />
                );
              })}

              {newRows.map((row, i) => {
                const rowNo = parcels.length + i + 1;
                return (
                  <ParcelEditRow
                    key={row.key}
                    error={row.error}
                    code={
                      <input
                        ref={(el) => {
                          if (el) inputRefs.current.set(row.key, el);
                          else inputRefs.current.delete(row.key);
                        }}
                        aria-label={t("orders.detail.parcels.codeRow", {
                          row: rowNo,
                        })}
                        className={parcelCellCls}
                        value={row.code}
                        onChange={(e) =>
                          patchNewRow(row.key, { code: e.target.value })
                        }
                        onBlur={() => void commitNewRow(row)}
                      />
                    }
                    description={
                      <input
                        aria-label={t("orders.detail.parcels.descriptionRow", {
                          row: rowNo,
                        })}
                        placeholder={t(
                          "orders.detail.parcels.descriptionPlaceholder",
                        )}
                        className={parcelCellCls}
                        value={row.description}
                        onChange={(e) =>
                          patchNewRow(row.key, { description: e.target.value })
                        }
                        onBlur={() => void commitNewRow(row)}
                      />
                    }
                    weight={
                      <input
                        aria-label={t("orders.detail.parcels.weightRow", {
                          row: rowNo,
                        })}
                        type="number"
                        min="0"
                        step="0.001"
                        className={`${parcelCellCls} px-2 tabular-nums`}
                        value={row.weight_kg}
                        onChange={(e) =>
                          patchNewRow(row.key, { weight_kg: e.target.value })
                        }
                        onBlur={() => void commitNewRow(row)}
                      />
                    }
                    action={
                      /* An unsaved row is discarded outright — there is nothing
                         on the server to confirm the deletion of. */
                      <button
                        type="button"
                        aria-label={t("orders.detail.parcels.removeRowNo", {
                          row: rowNo,
                        })}
                        onClick={() =>
                          setNewRows((rows) =>
                            rows.filter((r) => r.key !== row.key),
                          )
                        }
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-brand-danger/10 hover:text-brand-danger"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    }
                  />
                );
              })}
            </>
          )}
        </div>

        {/* Footer — «+ Посылка» left, «2 посылки · 0 кг» right (mockup). */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2">
          {canEdit ? (
            <button
              type="button"
              onClick={addRow}
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              {/* The visible label is «+ Посылка»; the accessible name is the
                  full «Добавить посылку», so nothing is lost to a screen
                  reader (mockup, 2026-08-25). */}
              <span aria-hidden="true">
                {t("orders.create.parcels.addRow")}
              </span>
              <span className="sr-only">{t("orders.detail.parcels.add")}</span>
            </button>
          ) : (
            <span />
          )}
          {/*
            ONE caption, between the button and the totals — it used to render
            here AND again under the footer whenever the block was editable,
            which both said the same thing twice and cost a row of the height
            budget. `truncate` keeps it from pushing the totals off at 1280.
          */}
          {barcodesFromOrderNumber && (
            <span className="min-w-0 flex-1 truncate text-right text-xs text-gray-400">
              {t("orders.detail.parcels.barcodesFromOrderNumber")}
            </span>
          )}
          <span className="shrink-0 text-xs font-semibold tabular-nums text-gray-700">
            {t("orders.detail.parcels.totals", {
              count: totalCount,
              weight: formatParcelWeight(totalWeight),
            })}
          </span>
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={t("orders.detail.parcels.delete")}
        description={t("orders.detail.parcels.deleteConfirm")}
        confirmLabel={t("orders.detail.parcels.delete")}
        cancelLabel={t("orders.detail.parcels.cancel")}
        variant="danger"
        onConfirm={() => {
          if (pendingDelete !== null) deleteMutation.mutate(pendingDelete);
        }}
      />
    </section>
  );
}
